import type {
  AuthSession,
  Capabilities,
  Lesson,
  JsonDocumentAnswer,
  PortalTrack,
  PreflightJudgmentInput,
  PracticeSession,
  PracticeState,
  StructuredAnswer,
  TrackListItem,
  WorkbenchDecision,
  WorkbenchEvent,
} from "../types";

const UNSAFE_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);
const UNAUTHORIZED_EVENT = "learning-portal:unauthorized";
let csrfToken = "";

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(message: string, status: number, code = "REQUEST_FAILED") {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

function csrfHeaders(init?: RequestInit): Headers {
  const headers = new Headers(init?.headers);
  if (init?.body !== undefined) headers.set("Content-Type", "application/json");
  if (UNSAFE_METHODS.has(init?.method || "GET") && csrfToken) {
    headers.set("X-CSRF-Token", csrfToken);
  }
  return headers;
}

function updateCsrf(payload: unknown): void {
  if (!payload || typeof payload !== "object") return;
  const value = "csrf_token" in payload ? payload.csrf_token : undefined;
  if (typeof value === "string") csrfToken = value;
}

function notifyUnauthorized(): void {
  csrfToken = "";
  if (typeof globalThis.dispatchEvent === "function" && typeof globalThis.CustomEvent === "function") {
    globalThis.dispatchEvent(new CustomEvent(UNAUTHORIZED_EVENT));
  }
}

async function errorFrom(response: Response): Promise<ApiError> {
  const body = await response.json().catch(() => ({}));
  const message = typeof body.error === "string" ? body.error : `请求失败 (${response.status})`;
  const code = typeof body.code === "string" ? body.code : "REQUEST_FAILED";
  if (response.status === 401) notifyUnauthorized();
  return new ApiError(message, response.status, code);
}

async function request<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    cache: "no-store",
    credentials: "same-origin",
    ...init,
    headers: csrfHeaders(init),
  });
  if (!response.ok) throw await errorFrom(response);
  const payload = await response.json() as T;
  updateCsrf(payload);
  return payload;
}

export const api = {
  authSession: async (): Promise<AuthSession> => request("/api/auth/session"),
  login: async (login: string, password: string): Promise<AuthSession> => request("/api/auth/login", {
    method: "POST",
    body: JSON.stringify({ login, password }),
  }),
  logout: async (): Promise<void> => {
    await request("/api/auth/logout", { method: "POST", body: JSON.stringify({}) });
    csrfToken = "";
  },
  capabilities: async (): Promise<Capabilities> => request("/api/capabilities"),
  tracks: async (): Promise<{ tracks: TrackListItem[]; version: string }> => request("/data/tracks.json"),
  track: async (slug: string): Promise<PortalTrack> => request(`/data/tracks/${encodeURIComponent(slug)}.json`),
  version: async (): Promise<{ version: string; generated_at: string }> => request(`/data/version.json?t=${Date.now()}`),
  practiceState: async (slug: string): Promise<PracticeState> => request(`/api/practice/state?track=${encodeURIComponent(slug)}`),
  lesson: async (slug: string, chapter: string): Promise<Lesson> => request(`/api/lessons/${encodeURIComponent(chapter)}?track=${encodeURIComponent(slug)}`),
  saveLessonProgress: async (slug: string, chapter: string, completed: boolean): Promise<void> => {
    await request(`/api/lessons/${encodeURIComponent(chapter)}/progress`, {
      method: "POST",
      body: JSON.stringify({ track_slug: slug, completed }),
    });
  },
  startSession: async (slug: string, options: { duration?: number; deck?: string; preflight_judgment?: PreflightJudgmentInput }): Promise<PracticeSession> => request("/api/practice/sessions", {
    method: "POST",
    body: JSON.stringify({ track_slug: slug, ...options }),
  }),
  session: async (id: string): Promise<PracticeSession> => request(`/api/practice/sessions/${encodeURIComponent(id)}`),
  answer: async (id: string, itemId: string, answer: string | number | StructuredAnswer | JsonDocumentAnswer, hintLevel: number): Promise<PracticeSession> => request(`/api/practice/sessions/${encodeURIComponent(id)}/answer`, {
    method: "POST",
    body: JSON.stringify({ item_id: itemId, answer, hint_level: hintLevel }),
  }),
  completeSession: async (id: string): Promise<PracticeSession> => request(`/api/practice/sessions/${encodeURIComponent(id)}/complete`, {
    method: "POST",
    body: JSON.stringify({}),
  }),
  workbenchEvents: async (slug: string): Promise<WorkbenchEvent[]> => {
    const payload = await request<{ events: WorkbenchEvent[] }>(`/api/practice/workbench-events?track=${encodeURIComponent(slug)}`);
    return payload.events;
  },
  decideFinding: async (slug: string, findingId: string, decision: WorkbenchDecision, reason: string): Promise<WorkbenchEvent> => {
    const payload = await request<{ event: WorkbenchEvent }>("/api/practice/workbench-events", {
      method: "POST",
      body: JSON.stringify({ track_slug: slug, finding_id: findingId, decision, reason }),
    });
    return payload.event;
  },
};
